import { useEffect, useState } from "react";

import { PiggyBank, User, Wallet } from "lucide-react";
import CustomLoader from "./CustomLoader";
import api from "../utils/axios";
import formatCurrency from "../helper/formatCurrency";




const BetHistoryDashboardNew = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [betHistory, setBetHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      setError("No token found. Please log in.");
      setLoading(false);
      return;
    }
    fetchUserApiBetHistory(page, limit);
  }, [page]);

  const fetchUserApiBetHistory = async (page, limit) => {
    setLoading(true);
    try {
      const response = await api.get(
        `/getUserApiBetHistory2?page=${page}&pageSize=${limit}`,
        {
          headers: {
            Authorization: `${token}`,
          },
        }
      );

      const { data, summary, pagination } = response.data;

      setBetHistory(data);
      setSummary(summary || null);
      setTotalPages(pagination.totalPages || 1);
      setPage(pagination.currentPage || 1);
    } catch (err) {
      console.error("Failed to fetch bet history:", err);
      setError("Failed to fetch bet history");
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage > 0 && newPage <= totalPages) {
      setPage(newPage);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-2xl font-bold text-gray-800">
          Bet History Dashboard
        </h1>
        <p className="text-sm text-gray-500">
          Manage your bet history here
        </p>
      </div>

      {loading ? (
        <CustomLoader />
      ) : error ? (
        <p className="text-red-600">{error}</p>
      ) : (
        <>
          {summary && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
              {/* Card Component */}
              {[
                {
                  icon: <User className="text-gray-800 h-5 w-5" />,
                  value: formatCurrency(summary.totalBetAmount),
                  title: "Total Bet Amount",
                  change: "+12%",
                  changeType: "up",
                },
                {
                  icon: <PiggyBank className="text-gray-800 h-5 w-5" />,
                  value: formatCurrency(summary.totalWinAmount),
                  title: "Total Win Amount",
                  change: "+12%",
                  changeType: "up",
                },
                {
                  icon: <Wallet className="text-gray-800 h-5 w-5" />,
                  value: formatCurrency(summary.totalLossAmount),
                  title: "Total Loss Amount",
                  change: "-28%",
                  changeType: "down",
                },
              ].map(({ icon, value, title, change, changeType }, i) => (
                <div
                  key={i}
                  className="borde p-5 bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 border"
                >
                  <div className="flex items-start justify-between">
                    <div className="bg-gray-300 p-2 rounded-md">{icon}</div>

                  </div>

                  <p className="text-white text-2xl font-bold mt-4">{value}</p>
                  <p className="text-gray-300 text-sm mt-1">{title}</p>


                </div>
              ))}
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="min-w-full border border-gray-200 divide-y divide-gray-200">
              <thead className="bg-gray-100 text-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-bold">#</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">User ID</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Game Provider</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Game Name</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Game Round</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Bet</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Win</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Currency</th>
                  <th className="px-4 py-3 text-left text-sm font-bold">Time</th>
                </tr>
              </thead>
              <tbody className="text-sm text-gray-800 divide-y divide-gray-200">
                {betHistory.map((item, index) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">{(page - 1) * limit + index + 1}</td>
                    <td className="px-4 py-3">{item.userCode}</td>
                    <td className="px-4 py-">{item.vendorCode}</td>
                    <td className="px-4 py-">{item.gameCode}</td>
                    <td className="px-4 py-3 font-mono text-xs">{item.roundId}</td>
                    {(() => {
                      const cur = (item?.detail?.currency || item.currency || "sc").toLowerCase();
                      const isGc = cur === "gc";
                      return (
                        <>
                          <td className="px-4 py-3 font-semibold text-slate-700">
                            {isGc ? `${Number(item.betAmount || 0).toLocaleString()} GC` : `${Number(item.betAmount || 0).toFixed(2)} SC`}
                          </td>
                          <td className={`px-4 py-3 font-bold ${Number(item.winAmount || 0) > 0 ? "text-green-600" : "text-slate-400"}`}>
                            {isGc ? `${Number(item.winAmount || 0).toLocaleString()} GC` : `${Number(item.winAmount || 0).toFixed(2)} SC`}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                              isGc ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800"
                            }`}>
                              {isGc ? "🪙 GC" : "✨ SC"}
                            </span>
                          </td>
                        </>
                      );
                    })()}
                    <td className="px-4 py-3 text-xs text-gray-500">{new Date(item.created_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-6 flex justify-center items-center space-x-4">
            <button
              onClick={() => handlePageChange(page - 1)}
              disabled={page === 1}
              className={`px-4 py-2 rounded ${page === 1
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Prev
            </button>
            <span className="text-gray-700">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </span>
            <button
              onClick={() => handlePageChange(page + 1)}
              disabled={page === totalPages}
              className={`px-4 py-2 rounded ${page === totalPages
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-blue-500 text-white hover:bg-blue-600"
                }`}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>

  );
};

export default BetHistoryDashboardNew;
